import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { toast } from '../../../../../../../ui/toast';
import { MainLayout } from '../../../../../../../ui';
import {
  getAssessment,
  getDetailedAssessmentResults,
  resultsPreScreen,
} from '../../../../../../../utils/api_service/assessment_api';
import { getBEIResults } from '../../../../../../../utils/api_service/Behavioral';
import {
  Assessment,
  AssessmentQuestionResult,
  AttemptSkill,
  PreScreenResults as PreScreenResultsType,
  User,
} from '../../../../../../../utils/types/assessment_types';
import { Tabs } from '../../../../tabs/tabs';
import { DetailedResults } from './Containers/DetailedResults';
import { PreScreenResults } from './Containers/PreScreenResults';
import { Summary } from './Containers/Summary';

interface BEIQuestionAnswer {
  questionId: string;
  question: string;
  answer: string;
  followupReason: string | null;
  videoFilename: string;
}

export interface BEIResultsData {
  assessmentId: string;
  userId: string;
  initialQuestion: string;
  attribute: string;
  questionAnswer: BEIQuestionAnswer[];
  confidence: number;
  score: number;
  followupCount: number;
  completed: boolean;
  starScore: {
    situation: number;
    task: number;
    action: number;
    result: number;
  };
  id: string;
}

export const AssessmentResultsDetails = () => {
  const RESULTS_TABS = ['Summary', 'Detailed Results', 'Pre-Screen Results'];
  const [activeTab, setActiveTab] = useState<number>(0);
  const [beiResults, setBeiResults] = useState<BEIResultsData[] | null>(null);
  const [assessmentData, setAssessmentData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<AttemptSkill[]>();
  const [detailedQuestions, setDetailedQuestions] = useState<
    AssessmentQuestionResult[]
  >([]);
  const [storedQuestions, setStoredQuestions] = useState<
    AssessmentQuestionResult[]
  >([]);
  const params =
    useParams<{ assessmentId: string; userId: string; id: string }>();
  const { assessmentId, userId, id } = params;

  // Extract ID from URL path as fallback
  const getIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    // URL structure: /view-assessment-result/assessmentId/userId/id
    return pathParts[4] || id; // 4th index for the id parameter
  };

  const finalId = id || getIdFromUrl();
  const [user, setUser] = useState<User>();
  const [assessmentDetails, setAssessmentDetails] = useState<Assessment>();
  const [preScreenResults, setPreScreenResults] = useState<
    PreScreenResultsType[]
  >([]);

  const isMounted = React.useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Fetch detailed assessment results with proper error handling
    const fetchAssessmentData = async () => {
      if (!finalId) {
        if (isMounted.current) {
          setError('Assessment result ID not found in URL');
          setLoading(false);
        }
        return;
      }

      try {
        if (isMounted.current) setLoading(true);
        const response = await getDetailedAssessmentResults(finalId);

        if (response && response.questions && response.questions.length > 0) {
          if (!isMounted.current) return;
          setSkills(response.skills);
          let newDetailedQuestions: AssessmentQuestionResult[] = [];
          let newStoredQuestions: AssessmentQuestionResult[] = [];

          response.questions.forEach((result: AssessmentQuestionResult) => {
            let questionResult = result;
            let startTime: number = 0;
            let endTime: number = 0;
            if (questionResult.timeStarted && questionResult.timeCompleted) {
              startTime =
                new Date(
                  questionResult.timeStarted.toString().split(' ').join('T'),
                ).getTime() / 1000;
              endTime =
                new Date(
                  questionResult.timeCompleted.toString().split(' ').join('T'),
                ).getTime() / 1000;
            }
            questionResult.completionTime = endTime - startTime;
            newDetailedQuestions.push(questionResult);
            newStoredQuestions.push(questionResult);
          });

          setDetailedQuestions(newDetailedQuestions);
          setStoredQuestions(newStoredQuestions);
          setUser(response.user);

          // Fetch assessment details
          try {
            const assessmentDetailsResponse = await getAssessment(
              response.questions[0].assessmentId,
            );
            if (isMounted.current)
              setAssessmentDetails(assessmentDetailsResponse);
          } catch (assessmentError) {
            console.warn(
              'Failed to fetch assessment details:',
              assessmentError,
            );
          }

          // Set the new assessment data format for DetailedResults
          if (isMounted.current) setAssessmentData(response);
        } else {
          if (isMounted.current) setAssessmentData(null);
        }
      } catch (error) {
        if (isMounted.current) {
          setAssessmentData(null);
          toast.error(
            'Failed to load assessment data. Some features may not be available.',
            {
              position: 'bottom-center',
            },
          );
        }
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    // Fetch pre-screen results with proper error handling
    const fetchPreScreenData = async () => {
      if (!finalId) {
        if (isMounted.current) setPreScreenResults([]);
        return;
      }

      try {
        const response = await resultsPreScreen(finalId);
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`,
          );
        }
        const data = await response.json();
        if (isMounted.current) setPreScreenResults(data);
      } catch (error) {
        // Don't show toast error for pre-screen since it's not critical
        if (isMounted.current) setPreScreenResults([]);
      }
    };

    fetchAssessmentData();
    fetchPreScreenData();
  }, [finalId]);

  useEffect(() => {
    const fetchBEIResults = async () => {
      // Manual extraction from URL as fallback
      const pathParts = window.location.pathname.split('/');
      const manualAssessmentId = pathParts[2]; // /view-assessment-result/[assessmentId]/[userId]/[id]
      const manualUserId = pathParts[3];

      // Use manual extraction if useParams fails
      const finalAssessmentId = assessmentId || manualAssessmentId;
      const finalUserId = userId || manualUserId;

      if (!finalAssessmentId || !finalUserId) {
        if (isMounted.current)
          setError('Assessment ID or User ID not found in URL');
        return;
      }

      try {
        if (isMounted.current) {
          setLoading(true);
          setError(null);
        }
        const response = await getBEIResults(finalAssessmentId, finalUserId);
        if (response.ok) {
          const data = await response.json();
          if (isMounted.current) setBeiResults(data);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch BEI results');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        if (isMounted.current) setError(errorMessage);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchBEIResults();
  }, [assessmentId, userId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className='space-y-6'>
            <Summary
                beiResults={beiResults}
                assessmentData={assessmentData}
                loading={loading}
                error={error}
            />
          </div>
        );
      case 1:
        return (
          <div className='space-y-6'>
            <DetailedResults
                beiResults={beiResults || []}
                assessmentData={assessmentData}
                loading={loading}
                error={error}
            />
          </div>
        );
      case 2:
        return (
          <div className='space-y-6'>
            <PreScreenResults
                preScreenResults={preScreenResults}
                loading={loading}
                error={error}
            />
          </div>
        );
      default:
        return (
          <div className='space-y-6'>
            <Summary
                beiResults={beiResults}
                assessmentData={assessmentData}
                loading={loading}
                error={error}
            />
          </div>
        );
    }
  };

  return (
    <>
      <div className='w-full h-full flex flex-col gap-5'>
        <nav
            className='flex w-full justify-between items-center border-b py-3 text-sm'
            aria-label='Breadcrumb'
        >
          <ol className='inline-flex items-center space-x-1 md:space-x-2'>
            <li>
              <Link
                  to={'/assessment-list'}
                  className='text-primary-textGray hover:underline'
              >
                Assessments
              </Link>
            </li>
            <li>
              <span className='text-gray-400'>/</span>
            </li>{ ' ' }
            <li>
              <Link
                  to={`/view-assessment/${assessmentId}`}
                  className='text-primary-textGray hover:underline'
              >
                Details
              </Link>
            </li>
            <li>
              <span className='text-gray-400'>/</span>
            </li>
            <li>
              <Link
                  to={'#'}
                  className='text-primary-dark font-semibold hover:underline'
              >
                Results
              </Link>
            </li>{ ' ' }
            <li>
              <span className='text-gray-400'>/</span>
            </li>
          </ol>
        </nav>
        <MainLayout>
          <>
            <Tabs
                tabTitles={RESULTS_TABS}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
            />
            { renderTabContent() }
          </>
        </MainLayout>
      </div>
    </>
  );
};
